import srApi from '@/services/api';
import NeedsFiltersResolver from "./NeedsFiltersResolver";
import userData from "@/services/clientUserData";
import sortAlphabeticallyAsc from '@/utils/sortAlphabeticallyAsc';

const api = srApi.create({ baseUrl: '/' });
export class SchoolSearchLoadingService {
  constructor(config) {
    this.config = config;
  }

  async boot() {
    const [ { aws_s3_url, school }, filterOptions ] = await Promise.all([
      this.getSchools(),
      this.getFilterOptions(),
    ]);

    return { aws_s3_url, school, filterOptions, needs: this.needs };
  }

  async getFilterOptions() {
    const [ regions, majors, divisions, conferences, needs ] = await Promise.all([
      this.getRegions(),
      this.getMajors(),
      this.getDivisions(),
      this.getConferences(),
      this.getNeeds(),
    ]);

    return {
      location: {
        regions: regions,
      },
      academics: {
        selectivity: this.getAcademicSelectivity(),
        enrollment: this.getUndergraduateEnrollment(),
        acceptanceRate: {},
        act: {},
        sat: {},
      },
      majors: majors,
      athletics: {
        divisions: divisions,
        conferences: conferences,
      },
      needs: needs
    };
  }

  async getSchools() {
    let { aws_s3_url, school } = await api.get(`/search/get_schools_with_programs`);

    school = school.filter(({mongo_id}) => String(mongo_id) !== "0");

    return { aws_s3_url, school };
  }

  async getConferences() {
    return this.formatConferences(await api.get(`/search/get_conferences`));
  }

  async getDivisions() {
    return await api.get(`/search/get_divisions`);
  }

  async getRegions() {
    return await api.get(`/search/get_regions`);
  }

  mapMajor(majorData, majorId, depth) {
    let major = {
      label: majorData.name,
      value: majorId,
      depth: depth,
    };

    if (majorData.child && Object.keys(majorData.child).length) {
      major.children = [];

      Object.keys(majorData.child).forEach(childId => {
        major.children.push(this.mapMajor(majorData.child[childId], childId, depth + 1));
      });
    }

    return major;
  }

  async getMajors() {
    let majors = [];
    let response = await api.get(`/search/get_majors`);

    Object.keys(response).forEach(majorId => {
      majors.push(this.mapMajor(response[majorId], majorId, 1));
    });

    return majors;
  }

  getAcademicSelectivity() {
    return [
      {
        label: 'Most Selective',
        value: 'most',
      },
      {
        label: 'Less Selective',
        value: 'less',
      },
      {
        label: 'More Selective',
        value: 'more',
      },
      {
        label: 'Least Selective',
        value: 'least',
      },
      {
        label: 'Selective',
        value: 'selective',
      },
    ];
  }

  getUndergraduateEnrollment() {
    return [
      { label: "Small", value: "small", description: "<3,000" },
      { label: "Medium", value: "medium", description: "3-10,000" },
      { label: "Large", value: "large", description: "10-20,000" },
      { label: "Extra Large", value: "x_large", description: ">20,000" },
    ]
  }

  async getNeeds() {
    if(!this.config.rosterNeedsFiltersEnabled) return [];
    
    let needs = [];
    const api = srApi.create();
    const { needs: needsRes, positions} = await api.get(`api/v2/school-search/needs`);

    this.needs = needsRes;
    this.positions = positions;
    
    const needsResolver = new NeedsFiltersResolver(needsRes, positions);

    if(userData.isPlayer) {
      needs.push(needsResolver.mapFullNeeds());
      needs.push(needsResolver.mapPotentialNeeds());
    }
    
    needs.push(needsResolver.mapAllRosterNeeds());

    return needs;
  }

  formatConferences(conferenceList) {
    const keys = Object.keys(conferenceList);
    const conferencesFormatted = Object
      .values(conferenceList)
      .map((label, index) => ({ value: keys[index].toString(), label }));

    return sortAlphabeticallyAsc(conferencesFormatted, "label");
  }
}

export default new SchoolSearchLoadingService();